import { type ReactNode } from 'react';
import { InlineLoading } from '@carbon/react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import styles from './output-section.module.scss';

/**
 * Props for the OutputSection component.
 */
export interface OutputSectionProps {
  /** Section title shown in the accordion header. */
  title: string;
  /** When true, an inline spinner shows beside the title (the section's loading umbrella). */
  isLoading?: boolean;
  /** Whether the section starts expanded (uncontrolled). */
  defaultExpanded?: boolean;
  /** Section content — banners + the section's main fixture. */
  children: ReactNode;
}

/**
 * A collapsible output-section card: an `Accordion` whose header is the section title
 * (with an optional inline loading spinner) and whose body holds the section content.
 *
 * Owns the overflow-taming that keeps wide tables/charts inside the accordion's animated
 * grid — `.details` forces `overflow: hidden` (the shared accordion sets `overflow: visible`
 * when expanded) and constrains the inner grid item — so consumers don't each re-copy that
 * fix (mirrors the pattern established by `type-curves/type-curve-wells-table`).
 *
 * @param {OutputSectionProps} props - Title, optional loading flag, default-expanded, children.
 * @return {JSX.Element} The accordion-wrapped section card.
 */
export function OutputSection({
  title,
  isLoading = false,
  defaultExpanded = false,
  children,
}: OutputSectionProps) {
  return (
    <Accordion className={styles.section} defaultExpanded={defaultExpanded}>
      <AccordionSummary>
        <div className={styles.headingRow}>
          <h2 className={styles.heading}>{title}</h2>
          {isLoading && (
            <InlineLoading
              className={styles.headingSpinner}
              description=""
              aria-hidden
            />
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails className={styles.details}>
        <div className={styles.body}>{children}</div>
      </AccordionDetails>
    </Accordion>
  );
}
